import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { api } from "@/lib/apiClient";
import { Search, Globe, Loader2 } from "lucide-react";

export default function LeagueExplorer() {
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [active, setActive] = useState(2);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/leagues?all=true").then((r) => setLeagues(r.data || [])); }, []);
  useEffect(() => {
    setLoading(true);
    api.get(`/teams?league=${active}`).then((r) => setTeams(r.data)).finally(() => setLoading(false));
  }, [active]);

  const activeLeague = leagues.find((l) => l.id === active);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    const base = t ? leagues.filter((l) => (l.name || "").toLowerCase().includes(t) || (l.country || "").toLowerCase().includes(t)) : leagues;
    return base.slice(0, 60);
  }, [q, leagues]);

  return (
    <div className="px-6 pb-24 pt-20 sm:px-10 lg:px-16">
      <p className="font-tac text-[11px] tracking-[0.35em] txt-cyan">LEARNING SOURCES · LIVE DATA</p>
      <h1 className="font-head mt-2 text-5xl font-black uppercase tracking-tighter">Training Grounds</h1>
      <p className="mt-2 max-w-xl text-sm text-white/50">
        Every real competition on earth — <span className="text-white">{leagues.length || "1,200+"}</span> leagues &amp; cups across
        <span className="text-white"> 170+</span> countries. Search any of them; OCTON studies real clubs to grow its tactical intelligence.
      </p>

      <div className="mt-6 flex items-center gap-2 rounded-xl border border-white/12 bg-[#0a0a0a] px-4 py-3 max-w-md" data-testid="league-search" data-octon-section="league search explorer competitions" data-octon-label="League Search">
        <Search size={16} className="text-white/40" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search 1,200+ leagues or a country…"
          data-testid="league-search-input" className="w-full bg-transparent text-sm text-white placeholder:text-white/30 outline-none" />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {filtered.map((l) => (
          <button key={l.id} data-testid={`league-${l.id}`} onClick={() => setActive(l.id)}
            className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm transition ${
              active === l.id ? "border-cyan-400/40 bg-cyan-400/10 text-white" : "border-white/10 text-white/50 hover:text-white"
            }`}>
            {l.flag ? <img src={l.flag} alt="" className="h-3 w-4.5 rounded-sm object-cover" /> : l.logo ? <img src={l.logo} alt="" className="h-4 w-4 object-contain" /> : <Globe size={13} className="text-white/30" />}
            {l.name}
            {l.country && !l.featured && <span className="text-white/30">· {l.country}</span>}
          </button>
        ))}
        {filtered.length === 0 && <p className="text-sm text-white/40">No league matches “{q}”.</p>}
      </div>

      <div className="mt-6 flex items-center gap-2 text-sm text-white/60">
        {activeLeague?.flag && <img src={activeLeague.flag} alt="" className="h-3.5 w-5 rounded-sm object-cover" />}
        <span className="font-head text-lg font-bold text-white">{activeLeague?.name || "League"}</span>
        {activeLeague?.country && <span className="text-white/40">· {activeLeague.country}</span>}
        <span className="text-white/30">· {teams.length} clubs</span>
      </div>

      {loading ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-octon-section="leagues grid competitions learned" data-octon-label="Leagues">
          {[...Array(6)].map((_, i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-white/5" />)}
        </div>
      ) : teams.length === 0 ? (
        <p className="mt-4 flex items-center gap-2 text-sm text-white/45"><Loader2 size={14} /> No club data for this competition (its season may be off, or the daily data quota is spent).</p>
      ) : (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {teams.map((t, i) => (
            <motion.div key={t.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.02, 0.5) }}
              className="card-tac flex items-center gap-4 p-5" data-testid={`club-${t.id}`}>
              <img src={t.logo} alt="" className="h-12 w-12 object-contain" />
              <div className="flex-1">
                <p className="font-head text-lg font-bold tracking-tight text-white">{t.name}</p>
                <p className="font-mono text-[11px] text-white/40">{t.code || `#${t.id}`}</p>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
